import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';

@Component({
  selector: 'app-deterministic-playbar',
  templateUrl: './deterministic-playbar.component.html',
  styleUrls: ['./deterministic-playbar.component.scss']
})
export class DeterministicPlaybarComponent implements OnDestroy, AfterViewChecked {

  // Whichever map service is configured for the current module (e.g. RainfallForecastMapService,
  // SeamlessRfViewMapService) — bound generically from gis-dims-map.component.html's
  // injectedService, so this component isn't tied to one specific feature module. It must expose
  // the same deterministic*-named fields/methods this component reads/calls below.
  @Input() dataSrv: any;

  @ViewChild('scrollRow', { static: false }) scrollRowEl: ElementRef<HTMLDivElement>;
  @ViewChildren('primaryBlockRef') primaryBlockRefs: QueryList<ElementRef<HTMLDivElement>>;
  private lastScrolledPrimaryGroup: string = null;

  readonly SCROLL_STEP = 220;

  isPlaying = false;
  private playTimer: any = null;

  get loadingGroup(): string {
    return this.dataSrv.deterministicLoadingGroup;
  }

  get activePrimaryGroup(): string {
    return this.dataSrv.deterministicActivePrimaryGroup;
  }

  get activeDayGroup(): string {
    const gis = this.dataSrv.gisMapComp;
    return gis ? gis.activeGroup : null;
  }

  // Shared tooltip, positioned via getBoundingClientRect() on hover — mirrors the lightning
  // playbar's tooltip (position: fixed, driven by mouseenter/mouseleave) rather than a per-block
  // CSS :hover popup, so it isn't clipped by the horizontally-scrolling dp-scroll-row.
  tooltip: { show: boolean; top: number; left: number; data: any; label?: string | null } = { show: false, top: 0, left: 0, data: null, label: null };

  getDaySegments(groupName: string): any[] {
    const entry = this.dataSrv.deterministicChartByGroup[groupName];
    return entry ? entry.segments : [];
  }

  showTooltip(event: MouseEvent, bars: any[], label?: string) {
    if (!bars.length) {
      return;
    }
    const el = (event.currentTarget || event.target) as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.tooltip = { show: true, top: rect.top - 8, left: rect.left + rect.width / 2, data: bars, label: label || null };
  }

  hideTooltip() {
    this.tooltip.show = false;
  }

  getSegmentHeight(value: number): string {
    const max = this.dataSrv.deterministicDayChartMax;
    if (!max || !value) {
      return '0%';
    }
    return ((value / max) * 100).toFixed(1) + '%';
  }

  // Flattens primary blocks + day blocks into one ordered row, with the day blocks for the
  // currently active primary block spliced in right after it (instead of appended at the end).
  // Each day item's segments/hasChart are computed once here rather than via template method
  // calls, which Angular would otherwise re-invoke on every change-detection pass.
  get rowItems(): any[] {
    const primaryBlocks = this.dataSrv.deterministicPrimaryBlocks || [];
    const dayBlocks = this.dataSrv.deterministicActiveDayBlocks || [];
    const items: any[] = [];
    for (const block of primaryBlocks) {
      items.push({ kind: 'primary', data: block });
      if (block.groupName === this.activePrimaryGroup) {
        for (const day of dayBlocks) {
          const segments = this.getDaySegments(day.groupName);
          items.push({ kind: 'day', data: day, segments, hasChart: segments.length > 0 });
        }
      }
    }
    return items;
  }

  trackByRowItem(index: number, item: any): string {
    return item.kind + '_' + (item.data ? item.data.groupName : index);
  }

  onPlayIconClick(event: MouseEvent) {
    event.stopPropagation();
    this.togglePlay();
  }

  selectPrimary(block: any) {
    const gis = this.dataSrv.gisMapComp;
    if (!gis || !block || this.loadingGroup) {
      return;
    }
    this.stopPlay();
    this.dataSrv.selectDeterministicPrimaryBlock(gis, block);
  }

  selectDay(dayBlock: any) {
    const gis = this.dataSrv.gisMapComp;
    if (!gis || !dayBlock) {
      return;
    }
    this.stopPlay();
    this.dataSrv.selectDeterministicDayBlock(gis, dayBlock.groupName);
  }

  togglePlay() {
    if (this.isPlaying) {
      this.stopPlay();
    } else {
      this.startPlay();
    }
  }

  private startPlay() {
    const dayBlocks = this.dataSrv.deterministicActiveDayBlocks || [];
    if (!dayBlocks.length) {
      return;
    }
    this.isPlaying = true;
    this.playTimer = setInterval(() => {
      const gis = this.dataSrv.gisMapComp;
      const currentDayBlocks = this.dataSrv.deterministicActiveDayBlocks || [];
      if (!gis || !currentDayBlocks.length) {
        this.stopPlay();
        return;
      }
      const currentIndex = currentDayBlocks.findIndex(d => d.groupName === this.activeDayGroup);
      const nextIndex = (currentIndex + 1) % currentDayBlocks.length;
      this.dataSrv.selectDeterministicDayBlock(gis, currentDayBlocks[nextIndex]['groupName']);
    }, 1200);
  }

  private stopPlay() {
    this.isPlaying = false;
    if (this.playTimer) {
      clearInterval(this.playTimer);
      this.playTimer = null;
    }
  }

  scrollLeft() {
    if (this.scrollRowEl) {
      this.scrollRowEl.nativeElement.scrollBy({ left: -this.SCROLL_STEP, behavior: 'smooth' });
    }
  }

  scrollRight() {
    if (this.scrollRowEl) {
      this.scrollRowEl.nativeElement.scrollBy({ left: this.SCROLL_STEP, behavior: 'smooth' });
    }
  }

  // activePrimaryGroup is a getter off dataSrv, not an @Input, so there's no ngOnChanges hook for
  // "the active hour changed" — ngAfterViewChecked runs after every CD cycle (covering both a
  // manual click and the async default-hour selection on load), and lastScrolledPrimaryGroup
  // ensures we only actually scroll once per genuine change, not on every cycle.
  ngAfterViewChecked() {
    if (this.activePrimaryGroup && this.activePrimaryGroup !== this.lastScrolledPrimaryGroup) {
      this.lastScrolledPrimaryGroup = this.activePrimaryGroup;
      this.scrollActivePrimaryIntoView();
    }
  }

  private scrollActivePrimaryIntoView() {
    const activeEl = this.primaryBlockRefs && this.primaryBlockRefs.find(
      ref => ref.nativeElement.dataset.groupName === this.activePrimaryGroup
    );
    if (activeEl) {
      activeEl.nativeElement.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  ngOnDestroy() {
    this.stopPlay();
    localStorage.removeItem('deterministicPlaybarIndex')
    localStorage.removeItem('deterministicPlaybarPrimaryGroup')
  }
}
